import React, { useState, useEffect } from 'react';
import { Eye, MessageCircle, Trash2 } from 'lucide-react';
import { User } from './models/User';
import MessageComposer from './components/MessageComposer';
import ContactModal from './components/ContactModal';
import { useMarketplace } from './hooks/useMarketplace';
import { formatCurrency, calculateTotal } from './utils/marketplaceUtils';
import { getAllUsers, getDisplayName } from './data/userDatabase.ts';
import { formatDate } from './utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useContactContext } from './contexts/ContactContext';

interface MarketplaceProps {
  user: User;
  onLogout: () => void;
}

const Marketplace: React.FC<MarketplaceProps> = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>({});
  
  // Use the comprehensive marketplace hook
  const marketplace = useMarketplace(navigate);

  // Load user display names
  useEffect(() => {
    const loadUserDisplayNames = async () => {
      const users = await getAllUsers();
      const displayNames: Record<string, string> = {};
      users.forEach((u: User) => {
        displayNames[u.id] = getDisplayName(u);
      });
      setUserDisplayNames(displayNames);
    };
    loadUserDisplayNames();
  }, []);

  // Extract functionality from the marketplace hook
  const {
    data: {
      sortedSalesOffers,
      sortedBuyRequests,
      highestBuyRequest,
      lowestSalesOffer,
      deleteSaleOffer,
      deleteBuyRequest,
      canUserDelete,
      addSaleOffer,
      addBuyRequest
    },
    contact: {
      contactModal,
      openContactBuyers,
      openContactSellers,
      closeContactModal,
      handleContactPerson,
      matches,
      canShowContactBuyers,
      canShowContactSellers
    },
    forms: {
      showBuyForm,
      showConfirmForm,
      showRequestToBuyForm,
      showRequestConfirmForm,
      formData,
      requestData,
      legalConfirmation,
      accuracyConfirmation,
      requestFundsConfirmation,
      requestAccuracyConfirmation,
      openSaleForm,
      openBuyRequestForm,
      closeForms,
      handleInputChange,
      handleRequestInputChange,
      handleSubmitInitialForm,
      handleSubmitRequestForm,
      setLegalConfirmation,
      setAccuracyConfirmation,
      setRequestFundsConfirmation,
      setRequestAccuracyConfirmation,
      submitSaleOffer,
      submitBuyRequest
    },
    modals: {
      selectedSaleOffer,
      selectedBuyRequest,
      showMessageComposer,
      selectedSeller,
      selectedBuyer,
      relatedEntityId,
      openSaleOfferDetails,
      openBuyRequestDetails,
      closeSaleOfferDetails,
      closeBuyRequestDetails,
      closeMessageComposer,
      handleMessageSend
    }
  } = marketplace;

  // Get the contact context for the new contact system
  const { contactUser } = useContactContext();

  // Enhanced contact handler that integrates with the new contact system
  const handleContactPersonEnhanced = (name: string, entityId: string) => {
    // First close the contact modal
    closeContactModal();
    
    // Then open the new contact modal with the specific user
    if (contactUser) {
      contactUser.openContactModal(name, 'Marketplace');
    }
  };

  const handleNavigation = (page: string) => {
    if (navigate) {
      navigate(page);
    }
  };

  const handleDeleteSaleOffer = async (offerId: string) => {
    if (confirm('Are you sure you want to delete this sale offer?')) {
      try {
        await marketplace.data.deleteSaleOffer(offerId);
      } catch (error) {
        console.error('Failed to delete sale offer:', error);
      }
    }
  };

  const handleDeleteBuyRequest = async (requestId: string) => {
    if (confirm('Are you sure you want to delete this buy request?')) {
      try {
        await marketplace.data.deleteBuyRequest(requestId);
      } catch (error) {
        console.error('Failed to delete buy request:', error);
      }
    }
  };

  const handleFinalSubmit = async () => {
    if (!accuracyConfirmation) return;
    try {
      await submitSaleOffer(user, addSaleOffer);
    } catch (error) {
      console.error('Failed to submit sale offer:', error);
              // Failed to submit sale offer
    }
  };

  const handleRequestFinalSubmit = async () => {
    if (!requestAccuracyConfirmation) return;
    try {
      await submitBuyRequest(user, addBuyRequest);
    } catch (error) {
      console.error('Failed to submit buy request:', error);
              // Failed to submit buy request
    }
  };

  // Helper function to get display name from user ID
  const getUserDisplayName = (userId: string): string => {
    return userDisplayNames[userId] || `User ${userId}`;
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Marketplace</h1>
            <p className="text-slate-600 mt-2">Buy and sell shares with other shareholders</p>
          </div>
          {user && !user.suspended && (
            <div className="flex gap-3">
              <button 
                onClick={openBuyRequestForm}
                className="border border-slate-300 text-slate-800 hover:bg-slate-50 hover:text-slate-900 h-auto px-4 py-1 text-sm font-medium rounded-lg"
              >
                Request to Buy
              </button>
              <button 
                onClick={openSaleForm}
                className="border border-slate-300 text-slate-800 hover:bg-slate-50 hover:text-slate-900 h-auto px-4 py-1 text-sm font-medium rounded-lg"
              >
                Offer for Sale
              </button>
            </div>
          )}
        </div>
        
        {/* Shares Available for Sale */}
        {sortedSalesOffers.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Shares for Sale</h2>
            <div className="grid gap-6">
              {sortedSalesOffers.map((offer) => (
                <div
                  key={offer.id}
                  className="bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl overflow-hidden"
                >
                  <div className="p-6">
                    {/* Header with Status and Actions */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6">
                      <div className="flex-1 mb-4 sm:mb-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-slate-900">
                            {offer.numberOfShares} Shares
                          </h3>
                          {parseFloat(offer.sharePrice) <= highestBuyRequest && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              Buyers Available
                            </span>
                          )}
                          {offer.seller === user.id && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              Your Offer
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm text-slate-600">
                          <span className="font-medium">{getUserDisplayName(offer.seller)}</span>
                          <span className="mx-2 text-slate-400">•</span>
                          <span>{formatDate(offer.timestamp)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Offer Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Price per Share</dt>
                        <dd className="mt-1 text-lg font-bold text-slate-900">{formatCurrency(offer.sharePrice)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Total Shares</dt>
                        <dd className="mt-1 text-lg font-bold text-slate-900">{offer.numberOfShares}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Total Value</dt>
                        <dd className="mt-1 text-lg font-bold text-slate-900">
                          {formatCurrency(calculateTotal(offer.sharePrice, offer.numberOfShares))}
                        </dd>
                      </div>
                    </div>

                    {/* Action Footer */}
                    <div className="flex flex-wrap gap-3">
                      <button 
                        onClick={() => openSaleOfferDetails(offer)}
                        className="flex items-center gap-2 h-8 px-3 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                      {canShowContactBuyers(offer, user, highestBuyRequest) && (
                        <button 
                          onClick={() => openContactBuyers(offer)}
                          className="flex items-center gap-2 h-8 px-3 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Contact Buyers
                        </button>
                      )}
                      {canUserDelete(offer, user) && (
                        <button 
                          onClick={() => handleDeleteSaleOffer(offer.id)}
                          className="flex items-center gap-2 h-8 px-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Buy Requests */}
        {sortedBuyRequests.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Buy Requests</h2>
            <div className="grid gap-6">
              {sortedBuyRequests.map((request) => (
                <div
                  key={request.id}
                  className="bg-white shadow-sm hover:shadow-md transition-all duration-200 border-0 rounded-xl overflow-hidden"
                >
                  <div className="p-6">
                    {/* Header with Status and Actions */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6">
                      <div className="flex-1 mb-4 sm:mb-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-slate-900">
                            {request.numberOfShares} Shares Wanted
                          </h3>
                          {parseFloat(request.sharePrice) >= lowestSalesOffer && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                              Sellers Available
                            </span>
                          )}
                          {request.requester === user.id && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              Your Request
                            </span>
                          )}
                        </div>
                        <div className="flex items-center text-sm text-slate-600">
                          <span className="font-medium">{getUserDisplayName(request.requester)}</span>
                          <span className="mx-2 text-slate-400">•</span>
                          <span>{formatDate(request.requestTimestamp)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Request Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Price per Share</dt>
                        <dd className="mt-1 text-lg font-bold text-slate-900">{formatCurrency(request.sharePrice)}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Shares Wanted</dt>
                        <dd className="mt-1 text-lg font-bold text-slate-900">{request.numberOfShares}</dd>
                      </div>
                      <div>
                        <dt className="text-sm font-medium text-slate-500">Total Value</dt>
                        <dd className="mt-1 text-lg font-bold text-slate-900">
                          {formatCurrency(calculateTotal(request.sharePrice, request.numberOfShares))}
                        </dd>
                      </div>
                    </div>

                    {/* Action Footer */}
                    <div className="flex flex-wrap gap-3">
                      <button 
                        onClick={() => openBuyRequestDetails(request)}
                        className="flex items-center gap-2 h-8 px-3 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                      {canShowContactSellers(request, user, lowestSalesOffer) && (
                        <button 
                          onClick={() => openContactSellers(request)}
                          className="flex items-center gap-2 h-8 px-3 text-sm font-medium text-green-600 hover:text-green-700 hover:bg-green-50 rounded-lg transition-colors"
                        >
                          <MessageCircle className="w-4 h-4" />
                          Contact Sellers
                        </button>
                      )}
                      {canUserDelete(request, user) && (
                        <button 
                          onClick={() => handleDeleteBuyRequest(request.id)}
                          className="flex items-center gap-2 h-8 px-3 text-sm font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Sale Form Modal */}
      {showBuyForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Offer Shares for Sale</h3>
            <form onSubmit={handleSubmitInitialForm}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="sharePrice" className="block text-sm font-medium text-slate-700 mb-1">
                    Share Price (USD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    id="sharePrice"
                    name="sharePrice"
                    value={formData.sharePrice}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="numberOfShares" className="block text-sm font-medium text-slate-700 mb-1">
                    Number of Shares
                  </label>
                  <input
                    type="number"
                    min="1"
                    id="numberOfShares"
                    name="numberOfShares"
                    value={formData.numberOfShares}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="legalConfirmation"
                      name="legalConfirmation"
                      type="checkbox"
                      checked={legalConfirmation}
                      onChange={(e) => setLegalConfirmation(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="legalConfirmation" className="font-medium text-gray-700">
                      I confirm that I am in possession of the shares offered and legally entitled to sell them
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={closeForms} 
                  className="h-8 px-3 text-sm border border-slate-300 rounded-md font-medium text-slate-700 bg-white hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!legalConfirmation}
                  className={`h-8 px-3 text-sm border rounded-md font-medium ${
                    legalConfirmation 
                      ? 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50' 
                      : 'border-slate-300 bg-slate-100 text-slate-400 cursor-not-allowed'
                  }`}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Form */}
      {showConfirmForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Information</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700">
                  I confirm that I am offering <strong>{formData.numberOfShares}</strong> shares for sale at {formatCurrency(formData.sharePrice)} per share.
                </p>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="accuracyConfirmation"
                    type="checkbox"
                    checked={accuracyConfirmation}
                    onChange={(e) => setAccuracyConfirmation(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="accuracyConfirmation" className="font-medium text-gray-700">
                    I confirm that all information is true and accurate
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                type="button" 
                onClick={closeForms} 
                className="h-8 px-3 text-sm border border-gray-300 rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleFinalSubmit}
                disabled={!accuracyConfirmation}
                className={`h-8 px-3 text-sm border rounded-md font-medium ${
                  accuracyConfirmation 
                    ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50' 
                    : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Buy Request Form Modal */}
      {showRequestToBuyForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Request to Buy Shares</h3>
            <form onSubmit={handleSubmitRequestForm}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="requestSharePrice" className="block text-sm font-medium text-gray-700 mb-1">
                    Share Price (USD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    id="requestSharePrice"
                    name="sharePrice"
                    value={requestData.sharePrice}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="requestNumberOfShares" className="block text-sm font-medium text-gray-700 mb-1">
                    Number of Shares
                  </label>
                  <input
                    type="number"
                    min="1"
                    id="requestNumberOfShares"
                    name="numberOfShares"
                    value={requestData.numberOfShares}
                    onChange={handleRequestInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>
                
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="requestFundsConfirmation"
                      name="requestFundsConfirmation"
                      type="checkbox"
                      checked={requestFundsConfirmation}
                      onChange={(e) => setRequestFundsConfirmation(e.target.checked)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="requestFundsConfirmation" className="font-medium text-gray-700">
                      I confirm that I have sufficient funds to buy the shares requested
                    </label>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end space-x-3">
                <button 
                  type="button" 
                  onClick={closeForms} 
                  className="h-8 px-3 text-sm border border-gray-300 rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!requestFundsConfirmation}
                  className={`h-8 px-3 text-sm border rounded-md font-medium ${
                    requestFundsConfirmation 
                      ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50' 
                      : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Buy Request Confirmation Form */}
      {showRequestConfirmForm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirm Buy Request</h3>
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-md">
                <p className="text-sm text-gray-700">
                  I confirm that I want to buy <strong>{requestData.numberOfShares}</strong> shares at {formatCurrency(requestData.sharePrice)} per share.
                </p>
              </div>
              
              <div className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id="requestAccuracyConfirmation"
                    type="checkbox"
                    checked={requestAccuracyConfirmation}
                    onChange={(e) => setRequestAccuracyConfirmation(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </div>
                <div className="ml-3 text-sm">
                  <label htmlFor="requestAccuracyConfirmation" className="font-medium text-gray-700">
                    I confirm that all information is true and accurate
                  </label>
                </div>
              </div>
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                type="button" 
                onClick={closeForms} 
                className="h-8 px-3 text-sm border border-gray-300 rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestFinalSubmit}
                disabled={!requestAccuracyConfirmation}
                className={`h-8 px-3 text-sm border rounded-md font-medium ${
                  requestAccuracyConfirmation 
                    ? 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50' 
                    : 'border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Message Composer */}
      {showMessageComposer && (selectedSeller || selectedBuyer) && (
        <MessageComposer
          recipient={(selectedSeller || selectedBuyer)!}
          relatedEntityId={relatedEntityId}
          onClose={closeMessageComposer}
          onSend={handleMessageSend}
        />
      )}

      {/* Sale Offer Details Modal */}
      {selectedSaleOffer && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Share Sale Offer Details</h3>
              <button 
                onClick={closeSaleOfferDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Seller</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getUserDisplayName(selectedSaleOffer.seller)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Shares Offered</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedSaleOffer.numberOfShares}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Price per Share</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatCurrency(selectedSaleOffer.sharePrice)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Value</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatCurrency(calculateTotal(selectedSaleOffer.sharePrice, selectedSaleOffer.numberOfShares))}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={closeSaleOfferDetails}
                  className="h-8 px-3 text-sm border border-gray-300 rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Buy Request Details Modal */}
      {selectedBuyRequest && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-10">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Buy Request Details</h3>
              <button 
                onClick={closeBuyRequestDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Requester</dt>
                    <dd className="mt-1 text-sm text-gray-900">{getUserDisplayName(selectedBuyRequest.requester)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Shares Wanted</dt>
                    <dd className="mt-1 text-sm text-gray-900">{selectedBuyRequest.numberOfShares}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Price per Share</dt>
                    <dd className="mt-1 text-sm text-gray-900">{formatCurrency(selectedBuyRequest.sharePrice)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Total Value</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatCurrency(calculateTotal(selectedBuyRequest.sharePrice, selectedBuyRequest.numberOfShares))}
                    </dd>
                  </div>
                </dl>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={closeBuyRequestDetails}
                  className="h-8 px-3 text-sm border border-gray-300 rounded-md font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contact Modal */}
      {contactModal.isOpen && contactModal.sourceItem && (
        <ContactModal
          type={contactModal.type}
          isOpen={contactModal.isOpen}
          onClose={closeContactModal}
          sourceItem={contactModal.sourceItem}
          matches={matches}
          onContact={handleContactPersonEnhanced}
        />
      )}
      </div>
    </div>
  );
};

export default Marketplace; 